import React, { useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import cx from 'classnames';
import { focusManager } from '@accedo/vdkweb-navigation';
import { Page } from '@accedo/vdkweb-tv-ui';
import { Image, Shimmer } from 'react-shimmer';
import GreenBackground from '#/static/images/background-images/WebHalfCircle.png';
import FullBackground from '#/static/images/background-images/GuestProfileBgGradation.png';
import {
  getWhiteLogoUri,
  getAgeGroupConfig,
  getGuestMetadata,
  getUnauthenticatedUserProfileContent,
} from '#/redux/modules/accedoOne/selectors';
import { isVIZIO, isTizen, isWorkstation } from '#/utils/componentStyleConfig';
import { actions as modalActions } from '#/redux/modules/modal/actions';
import {
  guestFooterDesc,
  guestFooterSignUpFree,
  guestProfileTitle,
  guestProfileScreenName,
  updatedGuestFooterDesc,
} from '#/config/strings';
import LoginRedirect from '#/components/AuthFlow/LoginRedirect/LoginRedirect';
import { authFlowRoutes, mainRoutes } from '#/utils/routes';
import {
  authScreenNames,
  ageCategoryGroup,
  ageRanges,
  guestTileTitle,
} from '#/config/constants';
import smallMonster from '#/static/images/icons/guests/small-monster.svg';
import mediumMonster from '#/static/images/icons/guests/medium-monster.svg';
import bigMonster from '#/static/images/icons/guests/big-monster.svg';
import {
  guestMode as sharedGuestMode,
  logout as sharedLogout,
} from '#/redux/modules/identity/actions';
import { guestMode } from '#/redux/modules/identity/identityUser/actions';
import { logout as guestLogout } from '#/redux/modules/identity/identityGuest/actions';
import { actions as onboardingActions } from '#/redux/modules/onboarding/actions';
import { actions as userActions } from '#/redux/modules/user/actions';
import { sendScreenView } from '#/redux/modules/firestore/actions';
// import { toggleSpinner } from '#/redux/modules/spinner/actions';
// import { getLoadingSpinnerState } from '#/redux/modules/spinner/selectors';

// Focus handling
import { FocusDiv, FocusAnchor, GuestFocusButton } from '#/widgets/ui';

import { isTTSEnabled } from '#/redux/modules/system/selectors';
import { setNativeFocus } from '#/utils/accessibility';

import {
  GUEST_PROFILE_SELECTION,
  HEADER,
  HOME,
} from '#/utils/navigationHelper';
import styles from './GuestProfile.scss';
// import { getAppStarted } from '#/redux/modules/lifecycle/selectors';

const {
  GUEST_PROFILE_PAGE,
  GUEST_PROFILE_CONTAINER,
  GUEST_PROFILE_FOOTER,
  SIGN_IN_LINK,
  PROFILE_CARDS_CONTAINER,
  PROFILE_CARD,
  SIGN_UP_LINK,
} = GUEST_PROFILE_SELECTION;

const { LIVETV, HOME: HOME_ROUTE } = mainRoutes;

// info not available in control
export const ageMetadata = {
  [`${ageCategoryGroup.MIN_AGE_TWO_UP}`]: {
    color: 'green',
    image: smallMonster,
    size: 'small',
    ageRange: ageRanges.TWO_UP,
  },
  [`${ageCategoryGroup.MIN_AGE_FIVE_UP}`]: {
    color: 'purple',
    image: mediumMonster,
    size: 'medium',
    ageRange: ageRanges.FIVE_UP,
  },
  [`${ageCategoryGroup.MIN_AGE_EIGHT_UP}`]: {
    color: 'yellow',
    image: bigMonster,
    size: 'big',
    ageRange: ageRanges.EIGHT_UP,
  },
};
const GuestCard = ({
  ageGroupValue = 0,
  contentImageUrl,
  tileDescription,
  typeOfContent,
  ageRange,
  info1,
  info2,
  info3,
  history,
  tagBackgroundColor,
  labelText,
  pillPosition,
  nav,
}) => {
  const { color = '' } = ageGroupValue ? ageMetadata[`${ageGroupValue}`] : {};
  const dispatch = useDispatch();
  const focusHomeAfterNavigation = () => {
    setTimeout(() => {
      focusManager.changeFocus(HEADER.MENU_ITEMS.HOME);
      focusManager.changeFocus(HOME.PAGE);
      focusManager.changeFocus(HOME.CONTAINER);
    }, 120);
  };

  const activateGuestMode = async age => {
    if (isTizen()) {
      // Tizen guard: force auth gate state before route transition.
      dispatch(userActions.enableGuestMode());
      dispatch(userActions.loginReceived());
      dispatch(guestMode({ age })).catch(error => {
        console.error('Failed to activate guest mode', error);
      });
      return;
    }

    await dispatch(sharedGuestMode({ age }));
  };

  const enterGuestPath = async ({ age, pathname, state }) => {
    try {
      await activateGuestMode(age);
    } catch (error) {
      console.error('Failed to activate guest mode', error);
    }

    history.replace('/');
    history.push({ pathname, state });

    if (isTizen() && pathname === HOME_ROUTE) {
      focusHomeAfterNavigation();
    }
  };

  const guestLiveTvByAge = async age => {
    await enterGuestPath({
      age,
      pathname: `${LIVETV}/${age}`,
    });
  };

  const renderImage = (imageUrl, label, zoneStyle) => {
    return (
      <div className={`${styles.imageZone} ${imageUrl ? zoneStyle : ''}`}>
        <>
          <div
            className={cx(styles.imageZoneCont, {
              [styles.tvImage]: isTizen() || isVIZIO(),
            })}
          >
            <Image src={imageUrl} fallback={<Shimmer />} />
          </div>
          {label && pillPosition && tagBackgroundColor && (
            <h6
              className={`${styles.tagCard} ${
                pillPosition === 'top-left' ? styles.topLeft : ''
              } ${pillPosition === 'bottom-left' ? styles.bottomLeft : ''} ${
                pillPosition === 'top-right' ? styles.topRight : ''
              } ${pillPosition === 'bottom-right' ? styles.bottomRight : ''}`}
              style={{ backgroundColor: tagBackgroundColor }}
            >
              {label}
            </h6>
          )}
        </>
      </div>
    );
  };

  return (
    <div aria-label={`${tileDescription}.`} className={styles.card}>
      <FocusDiv
        nav={nav}
        className={`${styles.cardTitle}`}
        classNameFocused={styles.tvFocused}
        onClick={() => {
          typeOfContent !== 'live'
            ? dispatch(
                modalActions.openModal({
                  modalId: 'AgeSelectionGuest',
                  modalData: {
                    ageRange,
                    color,
                    info1,
                    info2,
                    info3,
                  },
                  successAction: async () => {
                    await enterGuestPath({
                      age: ageGroupValue,
                      pathname: HOME_ROUTE,
                    });
                  },
                }),
              )
            : guestLiveTvByAge(ageGroupValue);
        }}
      >
        {renderImage(contentImageUrl, labelText, styles.desktopImageZone)}
        {tileDescription && (
          <h2 className={styles.desktopLabel}>{tileDescription}</h2>
        )}
      </FocusDiv>
    </div>
  );
};

GuestCard.propTypes = {
  ageGroupValue: PropTypes.number,
  ageRange: PropTypes.string,
  info1: PropTypes.string,
  info2: PropTypes.string,
  info3: PropTypes.string,
  nav: PropTypes.object,
  contentImageUrl: PropTypes.string,
  tileDescription: PropTypes.string,
  typeOfContent: PropTypes.string,
  history: PropTypes.object,
  tagBackgroundColor: PropTypes.string,
  labelText: PropTypes.string,
  pillPosition: PropTypes.string,
};

const Cards = ({ history, guestProfileContent }) => {
  const ageGroupConfig = useSelector(getAgeGroupConfig);

  const guestCards = useMemo(() => {
    const getAllKeyEntriesByContent = Object.keys(
      guestProfileContent?.setAllEntriesByContent ?? {},
    );
    const getAgeGroupConfigValues = Object.values(ageGroupConfig || []);

    return Object.values(guestProfileContent?.setAllEntriesByContent ?? {}).map(
      (groupContent, index, array) => {
        const guestCardsCountCount = array.reduce((t, a) => t + a.length, 0);
        return (
          <div
            className={styles.guestTitleText}
            key={getAllKeyEntriesByContent?.[index]}
          >
            <h2 className={styles.guestHorizontalLine}>
              {guestTileTitle[getAllKeyEntriesByContent?.[index]]}
            </h2>
            <div>
              {groupContent.map((group, idx) => {
                const {
                  ageGroupValue,
                  contentImageUrl,
                  contentImageLargeUrl,
                  contentImageSmallUrl,
                  tileDescription,
                  tileDescriptionMobile,
                  title,
                  typeOfContent,
                  tagBackgroundColor,
                  labelText,
                  labelTextTablet,
                  labelTextMobile,
                  pillPosition,
                } = group;
                const {
                  minAge = '',
                  maxAge = '',
                  noRegistrationConfig: { info1, info2, info3 } = {},
                } = ageGroupValue
                  ? getAgeGroupConfigValues.find(
                      c => c.ageGroupValue === ageGroupValue,
                    )
                  : {};
                const ageRange = minAge && maxAge ? `${minAge}-${maxAge}` : '';
                const cardIndex = index * (array.length + 1) + idx;
                return (
                  <GuestCard
                    history={history}
                    key={cardIndex}
                    nav={{
                      id: `${PROFILE_CARD}-${cardIndex}`,
                      nextleft:
                        cardIndex === 0
                          ? undefined
                          : `${PROFILE_CARD}-${cardIndex - 1}`,
                      nextright:
                        cardIndex + 1 === guestCardsCountCount
                          ? undefined
                          : `${PROFILE_CARD}-${cardIndex + 1}`,
                      nextdown: SIGN_IN_LINK,
                    }}
                    {...{
                      ageGroupValue,
                      contentImageUrl,
                      contentImageLargeUrl,
                      contentImageSmallUrl,
                      tileDescription,
                      tileDescriptionMobile,
                      title,
                      typeOfContent,
                      cardIndex,
                      ageRange,
                      info1,
                      info2,
                      info3,
                      tagBackgroundColor,
                      labelText,
                      labelTextTablet,
                      labelTextMobile,
                      pillPosition,
                    }}
                  />
                );
              })}
            </div>
          </div>
        );
      },
    );
  }, [guestProfileContent, ageGroupConfig]);

  return (
    <FocusDiv
      nav={{
        id: PROFILE_CARDS_CONTAINER,
        forwardFocus: `${PROFILE_CARD}-0`,
      }}
      className={styles.cardsArea}
    >
      {guestCards}
    </FocusDiv>
  );
};

Cards.propTypes = {
  history: PropTypes.object,
  guestProfileContent: PropTypes.object,
};

const GuestProfile = ({ history }) => {
  const dispatch = useDispatch();
  const logoUri = useSelector(getWhiteLogoUri) || '';
  const ttsEnabled = useSelector(isTTSEnabled);
  const guestMetadata = useSelector(state => getGuestMetadata(state)) || {};
  const fallbackGuestMetadata =
    useSelector(getUnauthenticatedUserProfileContent) || {};
  const guestProfileContent =
    guestMetadata?.totalCount > 0 ? guestMetadata : fallbackGuestMetadata;
  const parentSignInButtonText =
    guestProfileContent?.parentSignInButtonText || authScreenNames.SIGN_IN;
  const signUpButtonText =
    guestProfileContent?.signUpButtonText || authScreenNames.SIGN_UP;
  // const spinnerActive = useSelector(getLoadingSpinnerState);
  // const appStarted = useSelector(getAppStarted);
  const imageLoadRef = useRef(false);
  // const [hideSpinner, setHideSpinner] = useState(false);

  useEffect(() => {
    dispatch(sendScreenView(guestProfileScreenName));
  }, [guestProfileScreenName]);

  useEffect(() => {
    if (ttsEnabled) {
      setNativeFocus(GUEST_PROFILE_CONTAINER);
    }
  }, [ttsEnabled]);

  useEffect(() => {
    dispatch(onboardingActions.showGuestProfileImage(false));
    return () => {
      dispatch(onboardingActions.showGuestProfileImage(false));
    };
  }, []);

  // useEffect(() => {
  //   dispatch(toggleSpinner(!(appStarted && hideSpinner)));
  // }, [spinnerActive, appStarted, hideSpinner]);

  useEffect(() => {
    if (!guestProfileContent?.totalCount || imageLoadRef.current) {
      return;
    }

    imageLoadRef.current = true;

    const imageUrls = [
      ...Object.values(guestProfileContent.setAllEntriesByContent)
        .reduce((acc, c) => [...acc, ...c], [])
        .reduce((urls, { contentImageUrl }) => {
          urls.add(contentImageUrl);

          return urls;
        }, new Set()),
    ];

    Promise.allSettled([
      imageUrls.map(
        url =>
          new Promise(res => {
            const image = new Image();
            image.src = url;
            const callback = () => {
              image.removeEventListener('load', callback);
              image.removeEventListener('error', callback);
              res();
            };
            image.addEventListener('load', callback);
            image.addEventListener('error', callback);
          }),
      ),
    ]).then(() => {
      // setHideSpinner(true);
      dispatch(onboardingActions.showGuestProfileImage(true));
    });
  }, [guestProfileContent?.totalCount]);

  return (
    <Page
      nav={{
        id: GUEST_PROFILE_PAGE,
        forwardFocus: PROFILE_CARDS_CONTAINER,
      }}
    >
      <LoginRedirect />
      <div
        className={cx(styles.pageBackground, {
          [styles.isTv]: !isWorkstation(),
        })}
      >
        <div
          className={styles.profileSelection}
          style={{
            backgroundImage: `url(${
              isWorkstation() ? GreenBackground : FullBackground
            })`,
          }}
        >
          <FocusDiv
            role="main"
            nav={{ id: GUEST_PROFILE_CONTAINER }}
            className={styles.container}
            aria-label={`Sensical, ${guestProfileTitle}`}
            aria-describedby={`${PROFILE_CARDS_CONTAINER} ${GUEST_PROFILE_FOOTER}`}
          >
            <div className={styles.logoContainer}>
              <img src={logoUri} alt="Sensical" className={styles.logo} />
            </div>
            <Cards
              history={history}
              guestProfileContent={guestProfileContent}
            />
            {isWorkstation() && (
              <div>
                <p className={styles.footerText}>
                  Or&nbsp;
                  <FocusAnchor
                    nav={{
                      id: SIGN_UP_LINK,
                      nextup: PROFILE_CARDS_CONTAINER,
                    }}
                    onClick={async () => {
                      if (isTizen()) {
                        await dispatch(guestLogout(authFlowRoutes.AGE_GATE));
                      }
                      history.push(authFlowRoutes.AGE_GATE);
                    }}
                  >
                    {guestFooterSignUpFree}
                  </FocusAnchor>
                  &nbsp;{guestFooterDesc}
                </p>
              </div>
            )}
            {!isWorkstation() && (
              <div className={styles.tvFooter} id={GUEST_PROFILE_FOOTER}>
                <div>
                  <GuestFocusButton
                    className={styles.tvButton}
                    nav={{
                      id: SIGN_IN_LINK,
                      nextup: PROFILE_CARDS_CONTAINER,
                      nextright: SIGN_UP_LINK,
                    }}
                    onClick={async () => {
                      if (isTizen()) {
                        await dispatch(guestLogout(authFlowRoutes.SIGN_IN));
                      } else {
                        await dispatch(sharedLogout(authFlowRoutes.SIGN_IN));
                      }
                      history.push(authFlowRoutes.SIGN_IN);
                    }}
                    ariaLabel={parentSignInButtonText}
                  >
                    {parentSignInButtonText}
                  </GuestFocusButton>
                  <GuestFocusButton
                    className={styles.tvButton}
                    nav={{
                      id: SIGN_UP_LINK,
                      nextup: PROFILE_CARDS_CONTAINER,
                      nextleft: SIGN_IN_LINK,
                    }}
                    onClick={async () => {
                      const targetPath = isWorkstation()
                        ? authFlowRoutes.AGE_GATE
                        : authFlowRoutes.SIGN_UP;
                      if (isTizen()) {
                        await dispatch(guestLogout(targetPath));
                      } else {
                        await dispatch(sharedLogout(targetPath));
                      }
                      history.push(targetPath);
                    }}
                    ariaLabel={signUpButtonText}
                    aria-describedby={updatedGuestFooterDesc}
                  >
                    {signUpButtonText}
                  </GuestFocusButton>
                </div>
                <h4 className={cx(styles.tvFooterText)}>
                  {updatedGuestFooterDesc}
                </h4>
              </div>
            )}
          </FocusDiv>
          {isWorkstation() && (
            <div className={styles.signIn}>
              <FocusAnchor
                nav={{
                  id: SIGN_IN_LINK,
                  nextdown: PROFILE_CARDS_CONTAINER,
                }}
                onClick={async () => {
                  if (isTizen()) {
                    await dispatch(guestLogout(authFlowRoutes.SIGN_IN));
                  } else {
                    await dispatch(sharedLogout(authFlowRoutes.SIGN_IN));
                  }
                  history.push(authFlowRoutes.SIGN_IN);
                }}
              >
                {authScreenNames.SIGN_IN}
              </FocusAnchor>
            </div>
          )}
        </div>
      </div>
    </Page>
  );
};
GuestProfile.propTypes = {
  history: PropTypes.object,
};

export default withRouter(GuestProfile);
